import React, { useMemo } from 'react';

import Mousetrap from 'mousetrap';

import { Box } from '@atlaskit/primitives/compiled';
import { MiniModalLoadingFallbackComponent } from '@atlassian/conversation-assistant/ui/mini-modal-fallback';

import { useAISmartButtonContext } from '../../common/context';

const LazyAIMiniModal = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-mini-modal" */ './main').then((module) => ({
		default: module.AIMiniModal,
	})),
);

export const preloadAIMiniModal = (): void => {
	import(/* webpackChunkName: "@atlaskit-internal_ai-mini-modal" */ './main').then((module) => ({
		default: module.AIMiniModal,
	}));
};

export const AIMiniModal = (): React.JSX.Element => {
	const context = useAISmartButtonContext();
	const mousetrapRef = React.useRef<Mousetrap.MousetrapInstance | null>(null);
	const setContainerRef = React.useCallback((node: any) => {
		mousetrapRef.current?.reset();
		if (!node) {
			return;
		}
		mousetrapRef.current = new Mousetrap(node);
	}, []);

	const suppressKeyPress: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
		mousetrapRef.current?.bind(event.key.toLocaleLowerCase(), (e) => e.stopPropagation());
	};

	const miniModal = useMemo(() => {
		// 48px = nav header height, 8px = object sidebar bottom padding, 16px = AI smart button bottom margin, 8px = extra margin so it doesn't align with nav header
		const defaultTopOffset = 48 + 8 + 16 + 8;

		return (
			<React.Suspense
				fallback={
					<MiniModalLoadingFallbackComponent
						topOffset={context.topOffset || defaultTopOffset}
						isNav4Enabled={context.isNav4Enabled}
					/>
				}
			>
				<LazyAIMiniModal />
			</React.Suspense>
		);
	}, [context]);

	return (
		<Box
			ref={setContainerRef}
			onKeyDown={suppressKeyPress}
			testId="ai-smart-button-mini-modal-wrapper"
		>
			{context.overrideMiniModalWrapper ? context.overrideMiniModalWrapper(miniModal) : miniModal}
		</Box>
	);
};
